/**
 * The trading journal.
 *
 * A port of the sheet it replaces — same columns, same footer — with the two
 * things a spreadsheet footer cannot give you: how often the plan was actually
 * followed, and what departing from it cost.
 *
 * Nothing here reaches the ledger. A prop-firm balance is not money you own,
 * and folding an open P&L into net worth would make that figure swing on
 * positions that may be closed by lunchtime. When a payout arrives it is
 * entered as income, like any other income — that is the only point they meet.
 *
 * One renderer for both layouts: the table collapses to stacked cards below the
 * breakpoint rather than being written twice, because thirteen columns written
 * twice is thirteen columns to keep in step twice.
 */

import { h } from '../vdom.js';
import { C, MONO, css, card, kicker, input, select, button, removeButton, tap, planLimit, planLine } from './common.js';
import { tradeStats, disciplineGap, propStats, propRules } from '../../../shared/compute.js';
import { journalKpis, riskCard, tradeList, pnlCalendar } from './tradelog.js';
import { sessionStrip, sessionKpis, sampleCard, curveCard, sessionTrades, spreadCards } from './backtest.js';
import { DANGER_TEXT, DANGER_LINE, GAIN_BG, GAIN_LINE } from './whatif.js';
import { numberText, PROP_KINDS, PROP_RETURNS, parseNumber } from '../../../shared/model.js';

const GOLD = '#E3B86B';

export function renderJournal(ctx) {
  const { state, t, fmt, actions, inputs, mobile = false } = ctx;
  const s = t.shell;

  if (state.trades === null && !state.tradesFailed) {
    // Fetched on demand: a year of trades should not be loaded for somebody
    // who never opens this tab. `tradesFailed` is what keeps a failure from
    // turning this into a retry loop — see the store.
    actions.loadTrades();
  }
  /* The prop accounts too: a trade can be taken on one, and the form offers them. */
  if (state.prop === null && !state.propLoading) actions.loadProp();

  /*
   * Two halves over one list: the trades really taken, and the backtesting
   * sessions -- a strategy replayed on past charts. A trade belongs to one or
   * the other, and neither half's figures ever include the other's trades.
   */
  /* The prop-firm half has its own screen: accounts and money, not trades. */
  if (state.journalMode === 'prop') {
    return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: mobile ? '12px' : '18px' }) },
      h('div', { key: 'mode' }, modeSwitch(ctx, 'prop')),
      h('div', { key: 'prop' }, propView(ctx))
    );
  }

  const backtest = state.journalMode === 'backtest';
  const everything = state.trades || [];
  const sessions = state.backtests || [];
  const session = backtest
    ? (sessions.find(row => row.id === state.backtestId) || sessions[0] || null)
    : null;
  const trades = backtest
    ? (session ? everything.filter(row => row.backtestId === session.id) : [])
    : everything.filter(row => !row.backtestId);
  const stats = tradeStats(trades);

  /* Keyed throughout: switching halves adds and drops whole sections. */
  const part = (key, node) => node && h('div', { key }, node);
  const open = !backtest || Boolean(session);

  if (backtest) {
    return h('div', { class: 'pt-journal', style: css({ display: 'flex', flexDirection: 'column', gap: mobile ? '12px' : '14px' }) },
      part('mode', modeSwitch(ctx, 'backtest')),
      part('sessions', sessionStrip(ctx, { sessions, session, everything })),
      session && part('kpis', sessionKpis(trades, session, ctx)),
      session && part('sample', sampleCard(trades, session, ctx)),
      /* The curve and the trades beside how they spread, on a wide screen -- see style.css. */
      session && h('div', { key: 'bt-main', class: 'pt-journal-main' },
        h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }) },
          curveCard(trades, session, ctx),
          sessionTrades(trades, session, ctx)),
        h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }) },
          ...spreadCards(trades, session, ctx)))
    );
  }

  return h('div', { class: 'pt-journal', style: css({ display: 'flex', flexDirection: 'column', gap: mobile ? '12px' : '14px' }) },
    part('mode', modeSwitch(ctx, 'live')),
    part('summary', journalKpis(stats, trades, ctx)),
    part('runway', riskCard(everything, ctx)),
    /* Only for somebody who backtests: without sessions there is nothing to compare. */
    sessions.length > 0 && part('gap', gapCard(everything, ctx)),
    /* The list beside the calendar on a wide screen, under it on a narrow one -- see style.css. */
    h('div', { key: 'main', class: 'pt-journal-main' },
      tradeList(trades, ctx),
      pnlCalendar(trades, ctx))
  );
}

// ------------------------------------------------------------ backtests ----

/** Journal, backtesting or prop firms: which part of the tab is open. */
function modeSwitch({ t, actions, mobile }, mode) {
  const s = t.shell;
  const pill = (label, on, go, key) => h('div', {
    key,
    ...tap(go, label),
    class: 'pt-button',
    'aria-pressed': String(on),
    style: css({
      /* On a phone the three share the bar evenly, each label centred; on a desk they keep their own width. */
      flex: mobile ? '1 1 0' : '0 0 auto', minWidth: mobile ? 'max-content' : null, justifyContent: 'center',
      height: '40px', display: 'flex', alignItems: 'center', padding: mobile ? '0 12px' : '0 16px', borderRadius: '9px', cursor: 'pointer',
      fontSize: '13.5px', fontWeight: on ? 700 : 500, whiteSpace: 'nowrap',
      background: on ? C.line : 'transparent', color: on ? C.text : C.textDim,
      boxShadow: on ? `inset 0 0 0 1px ${C.accent}` : 'none'
    })
  }, label);

  /*
   * One row that scrolls sideways on a narrow phone rather than wrapping into
   * two. On a desk it is as wide as its buttons; on a phone it spans the
   * screen, and the buttons fill it rather than leaving a gap on the right.
   */
  return h('div', {
    style: css({
      display: 'flex', gap: '4px', padding: '4px', width: mobile ? '100%' : 'fit-content', boxSizing: 'border-box',
      maxWidth: '100%', overflowX: 'auto',
      border: `1px solid ${C.stroke}`, borderRadius: '12px', background: C.panel
    })
  },
    pill(s.jModeLive, mode === 'live', () => actions.setJournalMode('live'), 'live'),
    pill(s.jModeBacktest, mode === 'backtest', () => actions.setJournalMode('backtest'), 'backtest'),
    pill(s.jModeProp, mode === 'prop', () => actions.setJournalMode('prop'), 'prop')
  );
}

/*
 * The discipline gap (see `disciplineGap`): per setup, what the backtest
 * earned per trade, what real trading earns, and where the difference goes.
 */
function gapCard(trades, { t, fmt, mobile }) {
  const s = t.shell;
  const rows = disciplineGap(trades);
  const note = (text, color = C.faint) => h('div', {
    style: css({ fontSize: '11.5px', color, lineHeight: 1.5 })
  }, text);

  const figure = (label, value, color) => h('div', {
    style: css({ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 })
  },
    kicker(label.toUpperCase()),
    h('div', { style: css({ fontFamily: MONO, fontSize: mobile ? '15px' : '17px', fontWeight: 700, color }) }, value)
  );
  const tone = value => (value === null ? C.faint : (value >= 0 ? C.accent : C.danger));

  const setupRow = row => {
    const money = value => (value === null ? '—' : fmt.signedAmount(value, row.currency));
    const cost = value => fmt.amount(Math.abs(value), row.currency);
    return h('div', {
      key: row.setup,
      style: css({
        display: 'flex', flexDirection: 'column', gap: '10px', padding: '14px 0',
        borderTop: `1px solid ${C.line}`
      })
    },
      h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }) },
        h('div', { style: css({ fontSize: '13.5px', fontWeight: 700 }) }, row.setup),
        h('div', { style: css({ fontFamily: MONO, fontSize: '10px', color: C.faint }) },
          s.gapCounts(row.backtestCount, row.liveCount))
      ),
      row.mixed
        ? note(s.gapMixed)
        : h('div', {
          key: 'figures',
          style: css({ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px' })
        },
          figure(s.gapBacktest, money(row.backtestPerTrade), tone(row.backtestPerTrade)),
          figure(s.gapLive, money(row.livePerTrade), tone(row.livePerTrade)),
          figure(s.gapDiff, money(row.gap), tone(row.gap))
        ),
      !row.mixed && !row.enough && note(s.gapFew),
      !row.mixed && row.enough && row.gap >= 0 && note(s.gapAhead, C.accent),
      !row.mixed && row.enough && row.brokenCost > 0 && note(s.gapBroken(cost(row.brokenCost), row.brokenCount), C.textDim),
      !row.mixed && row.enough && row.tiltCost > 0 && note(s.gapTilt(cost(row.tiltCost), row.tiltCount), C.textDim)
    );
  };

  return card({ padding: mobile ? '14px' : '18px 20px', display: 'flex', flexDirection: 'column', gap: '8px' },
    h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }) },
      h('div', { style: css({ fontSize: '14px', fontWeight: 700 }) }, s.gapTitle),
      h('div', { style: css({ fontFamily: MONO, fontSize: '10px', color: C.faint }) }, s.gapSub)
    ),
    rows.length ? rows.map(setupRow) : note(s.gapHint)
  );
}

// ----------------------------------------------------------- prop firms ----

/*
 * Prop trading as a business: every account bought, what it cost and what it
 * paid back, and the balance on top -- the question the whole screen exists
 * to answer (see `propStats`).
 */
const STAGES = ['challenge', 'phase2', 'funded', 'failed'];
const STAGE_COLOR = { challenge: '#7AA2F7', phase2: '#B48EF0', funded: C.accent, failed: DANGER_TEXT, closed: C.muted };
const SPENT_BAR = '#8A3A40';

const capLine = (text, color = C.muted, key) => h('span', {
  key, style: css({ fontFamily: MONO, fontSize: '10.5px', letterSpacing: '1.2px', color })
}, text);

/* A labelled box, as the design draws them: the label over a 44px field. */
const labelled = (key, label, control) => h('label', { key, style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
  h('span', { style: css({ fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, label),
  control);
const FIELD = { height: '44px', boxSizing: 'border-box', width: '100%', borderRadius: '10px', padding: '0 12px', fontSize: '13.5px' };
const amountBox = (inputs, name, label, placeholder, unit, value, onEnter) => labelled(name, label,
  h('div', {
    style: css({
      display: 'flex', alignItems: 'center', gap: '6px', height: '44px', borderRadius: '10px', padding: '0 12px',
      border: `1px solid ${C.strokeHi}`, background: C.panel
    })
  },
    input(inputs, name, placeholder, {
      type: 'number', ariaLabel: label, value, onEnter,
      style: { flex: 1, minWidth: 0, background: 'transparent', border: 'none', padding: 0, fontFamily: MONO, fontSize: '14px' }
    }),
    h('span', { style: css({ fontFamily: MONO, fontSize: '11.5px', color: C.faint }) }, unit)));

/* A dashed row that opens something: a form, the rules. */
const dashed = (key, label, sub, go, extra = {}) => h('div', {
  key, ...tap(go, label), class: 'pt-button',
  style: css({
    minHeight: '48px', padding: '8px 14px', border: `1px dashed ${C.strokeHi}`, borderRadius: '12px', display: 'flex', flexWrap: 'wrap',
    alignItems: 'center', justifyContent: sub ? 'flex-start' : 'center', gap: '6px 12px', cursor: 'pointer', ...extra
  })
},
  h('span', { style: css({ fontFamily: MONO, fontSize: '12px', fontWeight: 700, letterSpacing: '1.2px', color: C.accent, whiteSpace: 'nowrap' }) }, label),
  sub && h('span', { key: 's', style: css({ flex: '1 1 200px', fontSize: '12.5px', color: C.muted, lineHeight: 1.45 }) }, sub));

const quiet = (label, go, color = C.muted) => h('div', {
  ...tap(go, label), class: 'pt-button',
  style: css({ height: '44px', display: 'flex', alignItems: 'center', padding: '0 14px', borderRadius: '9px', color, fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' })
}, label);

/* A row of choices on a dark track, one of them on. */
const track = (options, current, go, { columns = null, key } = {}) => h('div', {
  key, role: 'radiogroup',
  style: css({
    display: columns ? 'grid' : 'flex', gridTemplateColumns: columns ? `repeat(${columns}, minmax(0, 1fr))` : null, flexWrap: 'wrap',
    gap: '3px', padding: '3px', borderRadius: '10px', background: C.panel, border: `1px solid ${C.stroke}`
  })
}, ...options.map(([value, label, color]) => {
  const on = value === current;
  return h('div', {
    key: String(value), ...tap(() => go(value), label), role: 'radio', 'aria-checked': on ? 'true' : 'false',
    style: css({
      flex: columns ? null : '1 1 auto', height: '36px', minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 10px',
      borderRadius: '7px', background: on ? C.line : 'transparent', boxShadow: on ? `inset 0 0 0 1px ${color || C.strokeHi}` : 'none',
      fontSize: '12px', fontWeight: 600, color: on ? (color || C.text) : C.muted, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
    })
  }, label);
}));

function propView(ctx) {
  const { state, t, fmt, mobile } = ctx;
  const s = t.shell;
  if (state.prop === null) {
    ctx.actions.loadProp();
    return emptyNote(s.jLoading);
  }
  const accounts = state.prop;
  const p = propStats(accounts);
  const money = value => fmt.amount(Math.abs(value), p.currency);
  const signed = value => (value > 0.004 ? '+' : value < -0.004 ? '−' : '') + money(value);
  const totals = accounts.length > 0 && !p.mixed;

  /* The balance: what it all cost against what came back. */
  const net = totals ? p.net : 0;
  const up = totals && net > 0.004;
  const heroSub = !accounts.length ? s.propVerdictNone
    : p.mixed ? s.propMixed
      : net < -0.004 ? s.pfHeroDown(money(net), p.payouts ? Math.round(p.returns / p.costs * 100) : null)
        : up ? s.pfHeroUp(money(net))
          /* Nothing logged is not a break-even: "covered to the cent" on an empty account read as an achievement. */
          : !(p.costs > 0) && !(p.returns > 0) ? s.pfHeroNothing : s.pfHeroEven;
  const both = totals ? (p.costs + p.returns) || 1 : 1;
  const hero = h('div', {
    key: 'hero',
    style: css({
      padding: mobile ? '16px' : '18px 22px', borderRadius: '16px', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '14px 32px',
      border: `1px solid ${up ? GAIN_LINE : C.stroke}`, background: up ? GAIN_BG : C.card
    })
  },
    h('div', { style: css({ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }) },
      capLine(s.pfHeroCap),
      h('span', {
        style: css({
          fontFamily: MONO, fontSize: mobile ? '30px' : '40px', fontWeight: 700, letterSpacing: '-1px', whiteSpace: 'nowrap',
          color: !totals ? C.textDim : up ? C.accent : net < -0.004 ? DANGER_TEXT : C.text
        })
      }, totals ? signed(net) : '—'),
      h('span', { style: css({ fontSize: '12.5px', color: C.textDim, lineHeight: 1.45 }) }, heroSub)),
    totals && h('div', { key: 'bar', style: css({ flex: '2 1 360px', display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }) },
      h('div', { style: css({ display: 'flex', height: '10px', borderRadius: '5px', overflow: 'hidden', gap: '2px', background: C.stroke }) },
        h('div', { style: css({ width: (p.costs / both * 100).toFixed(1) + '%', background: SPENT_BAR }) }),
        h('div', { style: css({ width: (p.returns / both * 100).toFixed(1) + '%', background: C.accent }) })),
      h('div', { style: css({ display: 'flex', justifyContent: 'space-between', gap: '10px', fontFamily: MONO, fontSize: '11.5px' }) },
        h('span', { style: css({ color: DANGER_TEXT }) }, s.pfSpent(money(p.costs))),
        h('span', { style: css({ color: C.accentHi }) }, s.pfBack(money(p.returns))))));

  const kpi = (label, value, color, sub) => h('div', {
    key: label,
    style: css({
      padding: mobile ? '14px' : '16px 18px', border: `1px solid ${C.stroke}`, borderRadius: '14px', background: C.card,
      display: 'flex', flexDirection: 'column', gap: '7px', minWidth: 0
    })
  },
    capLine(label.toUpperCase()),
    h('div', { style: css({ fontFamily: MONO, fontSize: mobile ? '19px' : '24px', fontWeight: 700, letterSpacing: '-0.6px', color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, value),
    h('div', { style: css({ fontSize: '12px', color: C.textDim, lineHeight: 1.4 }) }, sub));
  const kpis = totals && h('div', {
    key: 'kpis', style: css({ display: 'grid', gridTemplateColumns: mobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: '12px' })
  },
    kpi(s.propCosts, p.costs ? '−' + money(p.costs) : money(0), p.costs ? DANGER_TEXT : C.textDim, s.propCostsSub),
    kpi(s.propReturns, p.returns ? '+' + money(p.returns) : money(0), p.returns ? C.accent : C.textDim, s.propReturnsSub(p.payouts)),
    /* Nothing back yet is "—", as the line under it says -- not −100%. */
    kpi(s.propRoi, p.roi === null || !p.returns ? '—' : (p.roi > 0 ? '+' : p.roi < 0 ? '−' : '') + fmt.percent(Math.abs(p.roi)),
      p.roi === null || !p.returns ? C.textDim : p.roi >= 0 ? C.accent : DANGER_TEXT,
      p.costPerPayout === null ? s.pfRoiLater : s.pfPerPayout(money(p.costPerPayout))),
    kpi(s.propPassed, p.passRate === null ? '—' : fmt.percent(p.passRate), C.text, s.pfPassedSub(p.passed, p.failed, p.active)));

  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: mobile ? '12px' : '16px' }) },
    hero,
    kpis,
    h('div', { key: 'head', style: css({ display: 'flex', alignItems: 'baseline', gap: '6px 12px', flexWrap: 'wrap', marginTop: '4px' }) },
      capLine(s.pfAccounts),
      h('span', { style: css({ fontSize: '12.5px', color: C.muted, lineHeight: 1.45 }) }, s.pfAccountsSub)),
    h('div', {
      key: 'accounts',
      style: css({ display: 'grid', gridTemplateColumns: mobile ? 'minmax(0, 1fr)' : 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: '14px', alignItems: 'start' })
    },
      ...accounts.map(account => h('div', { key: account.id, style: css({ minWidth: 0 }) }, propAccountCard(ctx, account))),
      h('div', { key: 'new', style: css({ gridColumn: '1 / -1' }) }, propNewAccount(ctx)))
  );
}

const PRESETS = [['Apex', '50K', 50000], ['Topstep', '50K', 50000], ['FTMO', '100K', 100000], ['MyFundedFutures', '50K', 50000]];

function propNewAccount({ t, inputs, actions, state }) {
  const s = t.shell;
  const ui = state.propUi || {};
  /* The free plan's accounts: those there are stay, closed ones included; only another is refused. */
  const limit = planLimit(state, 'propAccounts');
  const owned = (state.prop || []).length;
  const spent = limit !== null && owned >= limit;
  const plan = limit !== null && planLine(spent ? s.planPropSpent : s.planPropUsed(owned, limit), { spent, key: 'plan' });

  if (!ui.newOpen) {
    return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '8px' }) },
      dashed('open', s.pfNew, s.pfNewSub, () => (spent ? actions.showError('api:plan_prop') : actions.setPropUi({ newOpen: true })),
        { minHeight: '56px', justifyContent: 'center', textAlign: 'center' }),
      plan);
  }

  const add = () => {
    if (spent) return actions.showError('api:plan_prop');
    const firm = inputs.value('paFirm').trim();
    if (!firm) return actions.showError(s.propFirmNeeded);
    const fee = inputs.number('paFee');
    const day = localToday();
    actions.addPropAccount({
      firm,
      program: inputs.value('paProgram').trim(),
      size: inputs.number('paSize'),
      currency: inputs.value('paCurrency') || 'USD',
      started: day
    }, fee > 0 ? { amount: fee, date: day } : null);
    inputs.clear(['paFirm', 'paProgram', 'paSize', 'paFee']);
    actions.setPropUi({ newOpen: false });
  };
  const preset = ([firm, program, size]) => {
    const set = (name, value) => { const node = inputs.node(name); if (node) node.value = String(value); };
    set('paFirm', firm);
    set('paProgram', program);
    set('paSize', size);
    inputs.focus('paFee');
  };

  return h('div', {
    style: css({ padding: '16px', borderRadius: '14px', background: C.panel, border: `1px solid ${GAIN_LINE}`, display: 'flex', flexDirection: 'column', gap: '12px' })
  },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px' }) },
      capLine(s.pfNewTitle, C.accent),
      h('div', { style: css({ flex: 1 }) }),
      removeButton(() => actions.setPropUi({ newOpen: false }), { fontSize: '16px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }, s.cancel)),
    h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }) },
      labelled('firm', s.pfFirm, input(inputs, 'paFirm', s.pfFirmPh, { ariaLabel: s.pfFirm, style: FIELD, onEnter: add })),
      labelled('program', s.pfProgram, input(inputs, 'paProgram', s.pfProgramPh, { ariaLabel: s.pfProgram, style: FIELD, onEnter: add })),
      amountBox(inputs, 'paSize', s.pfSize, '50000', '', undefined, add),
      amountBox(inputs, 'paFee', s.pfFee, numberText('0.00'), '', undefined, add),
      labelled('cur', s.pfCurrency, select(inputs, 'paCurrency', ['USD', 'EUR', 'GBP', 'PLN'].map(code => ({ value: code, label: code })),
        { ...FIELD, fontFamily: MONO }, { ariaLabel: s.pfCurrency }))),
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }) },
      h('span', { style: css({ fontSize: '11.5px', color: C.muted, marginRight: '4px' }) }, s.pfPopular),
      ...PRESETS.map(row => h('div', {
        key: row[0], ...tap(() => preset(row), row[0] + ' ' + row[1]), class: 'pt-button',
        style: css({ height: '32px', display: 'flex', alignItems: 'center', padding: '0 10px', borderRadius: '7px', border: `1px solid ${C.stroke}`, fontSize: '12px', color: C.textDim, cursor: 'pointer', whiteSpace: 'nowrap' })
      }, row[0] + ' ' + row[1]))),
    plan,
    h('div', { style: css({ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '8px' }) },
      quiet(s.cancel, () => actions.setPropUi({ newOpen: false })),
      button(s.pfAdd, add, { style: { height: '44px', display: 'flex', alignItems: 'center', padding: '0 18px', opacity: state.busy ? 0.5 : 1 } })));
}

/* The reader's own date, not UTC's: a trade logged at 00:30 in Warsaw belongs to today. */
const localToday = () => {
  const now = new Date();
  return now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
};

/**
 * Where the account stands against its rules (see `propRules`): the way to
 * the target, the room left today and above the drawdown floor -- from the
 * journal trades taken on this account.
 */
function rulesBlock({ t, fmt, actions }, account) {
  const s = t.shell;
  const money = value => fmt.amount(Math.round(Math.abs(value)), account.currency);
  const signed = value => fmt.signedAmount(Math.round(value), account.currency);
  const r = propRules(account, account.trades, localToday());
  if (!r.hasRules) {
    return dashed('rules-none', s.pfRulesAdd, s.pfRulesAddSub, () => actions.openPropSettings(account));
  }

  /* Green with room, gold under 60%, red under 30%. */
  const tint = ratio => (ratio === null ? C.textDim : ratio < 0.3 ? DANGER_TEXT : ratio < 0.6 ? GOLD : C.accent);
  const bar = (key, label, value, ratio, color, sub) => h('div', { key, style: css({ display: 'flex', flexDirection: 'column', gap: '6px' }) },
    h('div', { style: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px' }) },
      h('span', { style: css({ fontSize: '12.5px', color: C.textDim }) }, label),
      h('span', { style: css({ fontFamily: MONO, fontSize: '12px', fontWeight: 700, color, whiteSpace: 'nowrap' }) }, value)),
    h('div', { style: css({ height: '6px', borderRadius: '3px', background: C.stroke, overflow: 'hidden' }) },
      h('div', { style: css({ width: Math.round(Math.max(0, Math.min(1, ratio || 0)) * 100) + '%', height: '100%', borderRadius: '3px', background: color }) })),
    h('span', { style: css({ fontSize: '11px', color: C.muted }) }, sub));

  const banner = r.breached ? [s.prBreached, DANGER_TEXT] : r.warning ? [s.prWarning, GOLD] : null;
  return h('div', {
    key: 'rules',
    style: css({
      display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px 14px', borderRadius: '12px', background: C.panel,
      border: `1px solid ${banner ? banner[1] : C.stroke}`
    })
  },
    h('div', { style: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px' }) },
      capLine(s.pfBalance),
      h('span', { style: css({ fontFamily: MONO, fontSize: '14px', fontWeight: 700 }) }, money(r.balance))),
    banner && h('div', { key: 'banner', role: 'status', style: css({ fontSize: '12.5px', lineHeight: 1.45, color: banner[1] }) }, banner[0]),
    r.target !== null && bar('target', s.pfTarget, signed(r.pnl) + ' / ' + money(r.target), r.targetRatio,
      r.reached ? C.accent : '#7AA2F7', r.reached ? s.pfTargetDone : s.pfTargetLeft(money(r.target - r.pnl))),
    r.dailyLoss !== null && bar('daily', s.pfDaily, s.pfLeft(money(Math.max(0, r.dailyRoom))), r.dailyRatio, tint(r.dailyRatio),
      s.pfDailySub(signed(r.todayPl), money(r.dailyLoss))),
    r.maxDrawdown !== null && bar('dd', s.pfDd, s.pfLeft(money(Math.max(0, r.ddRoom))), r.ddRatio, tint(r.ddRatio),
      s.pfDdSub(money(r.floor), s.prDdTypes[r.type])),
    !r.trades && h('div', { key: 'nt', style: css({ fontSize: '11.5px', color: C.muted, lineHeight: 1.45 }) }, s.prNoTrades));
}

/* The rules, the delete: what ✎ opens. */
function settingsPanel({ t, inputs, actions, state }, account) {
  const s = t.shell;
  const id = account.id;
  const rules = account.rules || {};
  const ui = state.propUi || {};
  const seed = value => (value ? numberText(value) : undefined);
  const save = () => {
    const read = key => {
      const raw = inputs.value(key + '_' + id).trim();
      return raw ? parseNumber(raw) : null;
    };
    const next = { dailyLoss: read('prDaily'), maxDrawdown: read('prMaxDd'), target: read('prTarget'), drawdownType: ui.ddType || 'eod' };
    for (const key of ['dailyLoss', 'maxDrawdown', 'target']) {
      if (next[key] !== null && !(next[key] > 0)) return actions.showError(s.amountNeeded);
    }
    actions.updatePropAccount(id, { rules: next });
    actions.setPropRulesEdit(null);
  };
  const confirm = state.propConfirm === id;

  return h('div', {
    key: 'settings',
    style: css({ padding: '14px', borderRadius: '12px', background: C.panel, border: `1px solid ${C.goldDeep}`, display: 'flex', flexDirection: 'column', gap: '12px' })
  },
    capLine(s.pfSettings, GOLD),
    h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }) },
      amountBox(inputs, 'prDaily_' + id, s.prDaily, s.pfDailyPh, '', seed(rules.dailyLoss), save),
      amountBox(inputs, 'prMaxDd_' + id, s.prMaxDd, s.pfDdPh, '', seed(rules.maxDrawdown), save),
      amountBox(inputs, 'prTarget_' + id, s.prTarget, s.pfTargetPh, '', seed(rules.target), save)),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '6px' }) },
      h('span', { style: css({ fontSize: '11.5px', color: C.muted }) }, s.prDdType),
      track(['static', 'eod', 'intraday'].map(value => [value, s.prDdTypes[value]]), ui.ddType || 'eod', value => actions.setPropUi({ ddType: value }))),
    h('div', { style: css({ fontSize: '11px', color: C.muted, lineHeight: 1.45 }) }, s.prTrailNote),
    confirm
      ? h('div', {
        key: 'confirm',
        style: css({ display: 'flex', flexDirection: 'column', gap: '10px', padding: '12px 14px', border: `1px solid ${DANGER_LINE}`, borderRadius: '10px' })
      },
        h('div', { style: css({ fontSize: '12.5px', lineHeight: 1.5 }) }, s.propDeleteAsk),
        h('div', { style: css({ display: 'flex', gap: '8px', flexWrap: 'wrap' }) },
          button(s.btDeleteYes, () => actions.removePropAccount(id), { style: { background: C.danger, color: C.bg, height: '44px', display: 'flex', alignItems: 'center' } }),
          quiet(s.cancel, () => actions.askDeletePropAccount(null))))
      : h('div', { key: 'buttons', style: css({ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }) },
        quiet(s.pfDelete, () => actions.askDeletePropAccount(id), DANGER_TEXT),
        h('div', { style: css({ flex: 1 }) }),
        quiet(s.cancel, () => actions.setPropRulesEdit(null)),
        button(s.prSave, save, { style: { height: '44px', display: 'flex', alignItems: 'center', padding: '0 18px' } })));
}

function propAccountCard(ctx, account) {
  const { t, fmt, inputs, actions, state } = ctx;
  const s = t.shell;
  const ui = state.propUi || {};
  const id = account.id;
  const money = value => fmt.amount(Math.abs(value), account.currency);
  const signed = value => (value > 0.004 ? '+' : value < -0.004 ? '−' : '') + money(value);
  const costs = account.events.filter(event => !PROP_RETURNS.includes(event.kind)).reduce((sum, event) => sum + Number(event.amount || 0), 0);
  const returns = account.events.filter(event => PROP_RETURNS.includes(event.kind)).reduce((sum, event) => sum + Number(event.amount || 0), 0);
  const net = returns - costs;
  const setOpen = state.propRulesEdit === id;
  const entryOpen = ui.entryFor === id;
  const title = [account.firm, account.program].filter(Boolean).join(' ');
  const meta = [account.size > 0 ? fmt.amount(account.size, account.currency) : null, account.started ? s.pfSince(fmt.day(account.started)) : null].filter(Boolean).join(' · ');
  const stages = account.status === 'closed' ? [...STAGES, 'closed'] : STAGES;
  const withTrades = { ...account, trades: (state.trades || []) };

  const log = () => {
    const amount = inputs.number('peAmount_' + id);
    if (!(amount > 0)) return actions.showError(s.propAmountNeeded);
    actions.addPropEvent(id, {
      date: inputs.value('peDate_' + id) || localToday(),
      kind: ui.kind || 'fee',
      amount,
      note: '',
      toLedger: ui.toLedger !== false
    });
    inputs.clear(['peAmount_' + id]);
    actions.setPropUi({ entryFor: null });
  };

  const entries = account.events.slice().sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const entryRow = event => {
    const back = PROP_RETURNS.includes(event.kind);
    const tag = !event.inLedger ? s.pfOnlyHere : back ? s.pfToIncome : s.pfToFees;
    return h('div', {
      key: event.id,
      style: css({ display: 'grid', gridTemplateColumns: '64px minmax(0, 1fr) auto 36px', gap: '10px', alignItems: 'center', minHeight: '48px', padding: '4px 0', borderBottom: `1px solid ${C.line}` })
    },
      h('span', { style: css({ fontFamily: MONO, fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap' }) }, fmt.day(event.date)),
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }) },
        h('span', { style: css({ fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, s.propKind[event.kind] || event.kind),
        h('span', { style: css({ fontSize: '11px', color: event.inLedger ? C.muted : C.faint }) }, tag)),
      h('span', { style: css({ fontFamily: MONO, fontSize: '13.5px', fontWeight: 700, color: back ? C.accent : DANGER_TEXT, whiteSpace: 'nowrap' }) },
        (back ? '+' : '−') + money(event.amount)),
      removeButton(() => actions.removePropEvent(event.id), {
        width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', fontSize: '16px'
      }, s.pfRemoveEntry));
  };

  const entryForm = h('div', {
    key: 'entry-form',
    style: css({ padding: '14px', borderRadius: '12px', background: C.panel, border: `1px solid ${C.stroke}`, display: 'flex', flexDirection: 'column', gap: '12px' })
  },
    h('div', { role: 'radiogroup', style: css({ display: 'flex', flexWrap: 'wrap', gap: '6px' }) },
      ...PROP_KINDS.map(kind => {
        const on = (ui.kind || 'fee') === kind;
        const back = PROP_RETURNS.includes(kind);
        return h('div', {
          key: kind, ...tap(() => actions.setPropUi({ kind }), s.propKind[kind]), role: 'radio', 'aria-checked': on ? 'true' : 'false',
          style: css({
            height: '36px', display: 'flex', alignItems: 'center', gap: '7px', padding: '0 12px', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap',
            border: `1px solid ${on ? (back ? GAIN_LINE : SPENT_BAR) : C.strokeHi}`, background: on ? C.line : 'transparent',
            fontSize: '12.5px', color: on ? C.text : C.textDim
          })
        },
          h('span', { style: css({ width: '7px', height: '7px', borderRadius: '50%', background: back ? C.accent : DANGER_TEXT }) }),
          s.propKind[kind]);
      })),
    h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }) },
      labelled('date', s.pfDate, input(inputs, 'peDate_' + id, '', {
        type: 'date', value: localToday(), ariaLabel: s.pfDate, style: { ...FIELD, fontFamily: MONO, fontSize: '13px', colorScheme: 'dark' }
      })),
      amountBox(inputs, 'peAmount_' + id, s.pfAmount, numberText('0.00'), account.currency, undefined, log)),
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }) },
      h('div', { style: css({ flex: '1 1 240px', minWidth: 0 }) },
        track([[true, s.pfToBudget], [false, s.pfOnlyHereOption]], ui.toLedger !== false, value => actions.setPropUi({ toLedger: value }))),
      quiet(s.cancel, () => actions.setPropUi({ entryFor: null })),
      button(s.pfLog, log, { style: { height: '44px', display: 'flex', alignItems: 'center', padding: '0 18px', opacity: state.busy ? 0.5 : 1 } })));

  return h('div', {
    style: css({
      minWidth: 0, padding: '16px 18px 18px', borderRadius: '14px', background: C.card, display: 'flex', flexDirection: 'column', gap: '14px',
      border: `1px solid ${account.status === 'funded' ? GAIN_LINE : account.status === 'failed' ? DANGER_LINE : C.stroke}`
    })
  },
    h('div', { style: css({ display: 'flex', alignItems: 'flex-start', gap: '10px' }) },
      h('div', { style: css({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }) },
        h('span', { style: css({ fontSize: '15px', fontWeight: 700, overflowWrap: 'anywhere' }) }, title),
        meta && h('span', { key: 'meta', style: css({ fontFamily: MONO, fontSize: '11.5px', color: C.muted }) }, meta)),
      h('div', { style: css({ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }) },
        h('span', { style: css({ fontFamily: MONO, fontSize: '15px', fontWeight: 700, whiteSpace: 'nowrap', color: net > 0.004 ? C.accent : net < -0.004 ? DANGER_TEXT : C.textDim }) }, signed(net)),
        h('span', { style: css({ fontSize: '11px', color: C.muted }) }, s.pfNet)),
      h('div', {
        ...tap(() => (setOpen ? actions.setPropRulesEdit(null) : actions.openPropSettings(account)), s.pfSettingsTitle),
        class: 'pt-button', title: s.pfSettingsTitle, 'aria-expanded': setOpen ? 'true' : 'false',
        style: css({
          width: '36px', height: '36px', flex: '0 0 36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px',
          border: `1px solid ${setOpen ? GOLD : C.stroke}`, color: setOpen ? GOLD : C.muted, fontSize: '12px', cursor: 'pointer'
        })
      }, '✎')),
    track(stages.map(value => [value, s.propStatus[value], STAGE_COLOR[value]]), account.status,
      status => status !== account.status && status !== 'closed' && actions.updatePropAccount(id, { status }), { columns: stages.length }),
    setOpen ? settingsPanel(ctx, account) : rulesBlock(ctx, withTrades),
    h('div', { key: 'entries', style: css({ display: 'flex', flexDirection: 'column' }) },
      h('div', { style: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingBottom: '6px', borderBottom: `1px solid ${C.strokeHi}` }) },
        capLine(s.pfEntries),
        h('span', { style: css({ fontFamily: MONO, fontSize: '11.5px', color: C.muted }) }, s.pfEntryCount(entries.length))),
      entries.length
        ? h('div', { key: 'list', style: css({ display: 'flex', flexDirection: 'column' }) }, ...entries.map(entryRow))
        : h('div', { key: 'none', style: css({ padding: '12px 0', fontSize: '12px', color: C.muted }) }, s.propNoEvents)),
    entryOpen ? entryForm : dashed('entry', s.pfEntryAdd, null,
      () => actions.setPropUi({ entryFor: id, kind: account.status === 'funded' ? 'payout' : 'fee' })));
}

const emptyNote = text => card({
  padding: '24px 20px', textAlign: 'center', color: C.faint, fontSize: '12.5px', lineHeight: 1.5
}, text);
